/* eslint-disable @typescript-eslint/no-explicit-any -- React.lazy is typed
   against `ComponentType<any>`; a narrower constraint here cannot be satisfied
   by an arbitrary page component. The `any` is confined to the generic bounds
   and never reaches a call site: `lazyLoad(() => import('./index'), m =>
   m.default)` still infers the component's real props. */
import React, { lazy, Suspense } from "react";

interface Opts {
  fallback: React.ReactNode;
}

type Unpromisify<T> = T extends Promise<infer P> ? P : never;

/**
 * Route-level code splitting. Usage lives in each container's `loadable.tsx`:
 *
 *   export const ParcelsPage = lazyLoad(
 *     () => import('./index'),
 *     (m) => m.default,
 *     { fallback: <LoadingIndicator fullPageHeight /> },
 *   );
 */
export const lazyLoad = <
  T extends Promise<any>,
  U extends React.ComponentType<any>,
>(
  importFunc: () => T,
  selectorFunc?: (s: Unpromisify<T>) => U,
  opts: Opts = { fallback: null }
) => {
  let lazyFactory: () => Promise<{ default: U }> = importFunc;

  if (selectorFunc) {
    lazyFactory = () =>
      importFunc().then((module) => ({ default: selectorFunc(module) }));
  }

  const LazyComponent = lazy(lazyFactory);

  return (props: React.ComponentProps<U>): JSX.Element => (
    <Suspense fallback={opts.fallback}>
      <LazyComponent {...props} />
    </Suspense>
  );
};
